import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext();

function getSessionId() {
  let sid = localStorage.getItem('cc_session_id');
  if (!sid) {
    sid = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('cc_session_id', sid);
  }
  return sid;
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [] });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');
  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/${sessionId}`);
      setCart(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching cart', err);
    }
  }, [sessionId]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/cart`, {
        session_id: sessionId,
        product_id: productId,
        quantity,
      });
      setCart(res.data);
      setIsOpen(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error adding to cart', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    try {
      const res = await axios.put(`${API}/cart/${sessionId}/items/${productId}`, { quantity });
      setCart(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error updating cart', err);
    }
  }, [sessionId]);

  const removeItem = useCallback(async (productId) => {
    try {
      const res = await axios.delete(`${API}/cart/${sessionId}/items/${productId}`);
      setCart(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error removing item', err);
    }
  }, [sessionId]);

  const applyPromo = useCallback(async (code) => {
    setPromoError('');
    try {
      const res = await axios.post(`${API}/promo/validate`, { code });
      setPromoApplied(res.data);
      setPromoCode(code);
      return true;
    } catch (err) {
      setPromoError('Invalid or expired promo code');
      setPromoApplied(null);
      return false;
    }
  }, []);

  const removePromo = useCallback(() => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  }, []);

  const checkout = useCallback(async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout`, {
        session_id: sessionId,
        origin_url: origin,
        promo_code: promoApplied ? promoApplied.code : null,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Checkout error', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId, promoApplied]);

  const cartCount = cart.items ? cart.items.reduce((sum, i) => sum + i.quantity, 0) : 0;
  const cartSubtotal = cart.items ? cart.items.reduce((sum, i) => sum + i.price * i.quantity, 0) : 0;
  const discount = promoApplied ? Math.round(cartSubtotal * (promoApplied.discount_percent / 100) * 100) / 100 : 0;
  const cartTotal = Math.max(cartSubtotal - discount, 0);

  return (
    <CartContext.Provider value={{
      cart, cartCount, cartSubtotal, cartTotal, discount, isOpen, setIsOpen, loading,
      addToCart, updateQuantity, removeItem, checkout, fetchCart, sessionId,
      promoCode, setPromoCode, promoApplied, promoError, applyPromo, removePromo,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
